<template>
  <div class="row justify-center">
    <QSpinner
      v-if="loading"
      class="q-mt-smd"
      :size="30"
    />
    <div
      v-else
      class="column full-height"
    >
      <QInfiniteScroll
        ref="infiniteTweetScroll"
        class="infinite-tweets-container"
        @load="loadMoreTweets"
      >
        <!--Header-->
        <div class="card q-pa-smd smaller">
          <!-- Publisher & Live Tag -->
          <div class="row q-mb-sm no-wrap items-start justify-start">
            <img
              :alt="$t('article_preview.source_logo')"
              class="logo"
              :src="onlineArticle.source.logo_url"
            />
            <!-- Title -->
            <span
              class="pointer"
              @click.prevent="openExternalUrlForMention(onlineArticle, $router)"
            >
              <strong class="q-mb-sm q-pr-md bigger2 e2e-mention-title block">
                {{ onlineArticle.title }}
              </strong>
              <StreemTooltip>{{
                $t("article_preview.tooltips.read_full_article")
              }}</StreemTooltip>
            </span>
          </div>
          <!-- Publisher -->
          <div class="row column no-wrap q-mb-sm">
            <div class="row items-center">
              <div
                v-if="isOnFrontPage"
                class="breaking row smaller q-mr-sm"
              >
                <div>{{ $t("article_preview.front_page") }}</div>
                <div class="q-ml-sm duration">
                  {{ frontPageDuration }}
                </div>
                <StreemTooltip>
                  {{
                    $t("article_preview.tooltips.front_page_rank", {
                      rank: currentRank,
                      date: formatDate(oldestRanking.created_at, "h:mmaaa"),
                    })
                  }}
                </StreemTooltip>
              </div>
              <strong
                v-if="siblings.length > 1"
                class="pointer"
                @click.stop="displaySyndications = !displaySyndications"
              >
                {{ onlineArticle.source.name }} + {{ siblings.length }}
                <StreemTooltip>{{
                  $t("article_preview.tooltips.view_syndications")
                }}</StreemTooltip>
              </strong>
              <strong v-else>
                {{ onlineArticle.source.name }}
              </strong>
            </div>
            <span class="soft">
              {{ timeAgo(onlineArticle.published_at) }}
              <AuthorWidget
                v-if="onlineArticle.author"
                :mention="onlineArticle"
              />
              ·
              <template v-if="onlineArticle.page_number">
                {{
                  $t("article_preview.word_count_and_page_number", {
                    wordCount: prettyNumber(onlineArticle.word_count),
                    pageNumber: onlineArticle.page_number,
                  })
                }}
              </template>

              <template v-else>
                {{
                  $t("article_preview.word_count", {
                    wordCount: prettyNumber(onlineArticle.word_count),
                  })
                }}
              </template>
            </span>
          </div>

          <!--Syndications-->
          <div
            v-if="displaySyndications"
            class="q-pa-sm q-my-sm bg-silver-white column scroll no-wrap"
            style="max-height: 110px"
          >
            <span
              v-for="sibling in siblings"
              :key="sibling.id"
              class="row pointer no-shrink justify-between"
              @click.prevent.stop="openExternalUrlForMention(sibling, $router)"
            >
              <span class="bold">
                {{ sibling.source.name }}
              </span>
              <span class="soft smaller">
                {{ formatDate(sibling.published_at, "h:mmaaa") }}
              </span>
            </span>
          </div>

          <!--Body-->
          <p>
            <!-- eslint-disable vue/no-v-html -->
            <span
              class="highlightable"
              v-html="highlightedBody"
            />
            <span
              @click.prevent.stop="
                openExternalUrlForMention(onlineArticle, $router)
              "
            >
              <span>&nbsp;{{ $t("article_preview.read_more") }}</span
              >&nbsp;<QIcon
                class="soft"
                name="ion-md-open"
              /><StreemTooltip>{{
                $t("article_preview.tooltips.read_full_article")
              }}</StreemTooltip>
            </span>
          </p>

          <!--Footer (front page ranking, social)-->
          <span>
            <StreemTooltip
              anchor="bottom middle"
              self="top middle"
            >
              {{ $t("article_preview.tooltips.change_data_source") }}
            </StreemTooltip>

            <BaseButtonDropdown
              v-if="siblings.length > 1"
              align="bottom-start"
              chevron
              close-on-click
              flat
              icon="ion-trending-up"
              :label="
                $t('article_preview.data_for_source', {
                  sourceName: (selectedSibling || onlineArticle).source.name,
                })
              "
              padding="xs"
              submenu
            >
              <div class="dropdown-list">
                <span
                  v-for="sibling in siblings"
                  :key="sibling.id"
                  class="item"
                  :class="{
                    selected:
                      (selectedSibling || onlineArticle).id === sibling.id,
                  }"
                  @click="selectSibling(sibling)"
                >
                  {{ sibling.source.name }}
                </span>
              </div>
            </BaseButtonDropdown>
          </span>

          <div class="row q-mt-sm">
            <div class="column col-6">
              <h2 class="text-tv no-margin">
                {{ $t("article_preview.front_page") }}
              </h2>
              <QSpinner
                v-if="loadingFrontPages"
                class="q-mt-sm"
                size="20px"
              />
              <h1 v-else-if="!isOnFrontPage">-</h1>
              <template v-else>
                <h1
                  class="no-margin"
                  style="font-size: 35px"
                >
                  {{ frontPageDuration }}
                </h1>
                <span class="smaller soft">
                  {{
                    $t("article_preview.current_front_page_position", {
                      currentRank,
                      date: formatDate(oldestRanking.created_at, "h:mmaaa"),
                    })
                  }}
                </span>
              </template>
            </div>
            <div class="column col-6">
              <h2 class="text-tv no-margin">
                {{ $t("article_preview.social") }}
              </h2>
              <QSpinner
                v-if="loadingSocial"
                class="q-mt-sm"
                size="20px"
              />
              <h1
                v-else
                class="no-margin"
                style="font-size: 35px"
              >
                {{ prettyNumber(socialCount.total) }}
              </h1>
            </div>
          </div>
          <div class="row q-mt-lg column">
            <div class="row">
              <div class="column col-8 q-pt-sm">
                <p class="no-margin q-mb-sm q-pr-md bigger bold">
                  {{ $t("article_preview.tweets_about_article") }}
                </p>
              </div>
            </div>
            <div class="tweets-container">
              <span
                v-if="!tweets.length"
                class="no-margin soft smaller block"
              >
                {{ $t("article_preview.no_tweets_found") }}
              </span>
              <Tweet
                v-for="tweetID in tweets"
                :id="tweetID.toString()"
                :key="tweetID"
                class="embedded-tweet"
                :error-message="$t('article_preview.tweet_not_found')"
                :options="{
                  conversation: 'none',
                  width: 313,
                  dnt: true,
                  cards: 'hidden',
                }"
              />
              <QSpinnerDots
                v-if="tweetFeedLoading"
                class="infinite-tweets-spinner"
                size="0.8em"
              />
            </div>
          </div>
        </div>
      </QInfiniteScroll>
    </div>
  </div>
</template>

<script>
import { Tweet } from "@jacksongross/vue-tweet-embed";
import differenceInMinutes from "date-fns/differenceInMinutes";
import { uniq } from "lodash-es";

import AuthorWidget from "shared/components/AuthorWidget.vue";
import { BaseButtonDropdown } from "shared/components/base";
import { formatDate, timeAgo } from "shared/helpers/date";
import { openExternalUrlForMention } from "shared/helpers/external";
import { prettyNumber } from "shared/helpers/number";
import { highlight, nl2br } from "shared/helpers/string";

export default {
  name: "ArticlePreview",
  components: {
    AuthorWidget,
    BaseButtonDropdown,
    Tweet,
  },
  props: {
    articleType: {
      type: String,
      default: "",
      required: false,
    },
    id: {
      type: [Number, String],
      default: "",
      required: false,
    },
    keywords: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  data() {
    return {
      openExternalUrlForMention,
      onlineArticle: {},
      loading: true,
      loadingFrontPages: true,
      loadingSocial: true,
      active: false,
      siblings: [],
      frontPageRankings: [],
      socialCount: {},
      displaySyndications: false,
      selectedSibling: null,
      tweets: [],
      tweetsPage: 0,
      tweetFeedLoading: true,
    };
  },
  computed: {
    isOnFrontPage() {
      return (
        this.frontPageRankings.length &&
        differenceInMinutes(
          new Date(),
          new Date(this.frontPageRankings.slice(-1).pop().created_at)
        ) < 15
      );
    },
    frontPageDuration() {
      const minutes =
        this.frontPageRankings.length > 1
          ? differenceInMinutes(
              new Date(this.frontPageRankings.slice(-1).pop().created_at),
              new Date(this.frontPageRankings[0].created_at)
            )
          : 5;

      return minutes >= 60 ? `${Math.round(minutes / 60)}h` : `${minutes}m`;
    },
    highlightedBody() {
      return highlight(nl2br(this.onlineArticle.excerpt), this.keywords);
    },
    oldestRanking() {
      return this.frontPageRankings[0];
    },
    latestRanking() {
      return this.frontPageRankings[this.frontPageRankings.length - 1];
    },
    currentRank() {
      return this.latestRanking.rank;
    },
  },
  async mounted() {
    if (this.$isMobile)
      this.$navBar.buttonClicked(
        "show-action-sheet-article",
        this.showActionSheet
      );
    if (this.articleType === "article")
      this.$track("Article Seen", { id: this.id });

    const responses = await Promise.all([
      this.$streemApiV1.get(`${this.articleType}s/${this.id}`),
      this.$streemApiV1.get(`articles/${this.id}/siblings`),
    ]);

    this.onlineArticle = { ...responses[0].data, type: this.articleType };
    this.siblings = responses[1].data;
    this.loading = false;

    // Load less important data
    this.loadExtraData(this.onlineArticle);
  },
  activated() {
    this.active = true;
  },
  deactivated() {
    this.active = false;
  },
  destroy() {
    this.$refs.infiniteTweetScroll.stop();
  },
  methods: {
    formatDate,
    prettyNumber,
    timeAgo,
    showActionSheet() {
      if (!this.active) {
        return;
      }

      this.$refs.actionSheetMention.show();
    },
    selectSibling(sibling) {
      this.selectedSibling = sibling;
      this.loadExtraData(sibling);
    },
    async loadExtraData(sibling) {
      await Promise.all([
        this.loadFrontPages(sibling),
        this.loadSocialCounts(sibling),
      ]);
    },
    async loadFrontPages(sibling) {
      this.loadingFrontPages = true;

      try {
        const frontPageRankings = (
          await this.$streemApiV1.get(
            `articles/${sibling.id}/front_page_rankings`
          )
        ).data;

        this.frontPageRankings = frontPageRankings || [];
      } finally {
        this.loadingFrontPages = false;
      }
    },
    async loadSocialCounts(sibling) {
      this.loadingSocial = true;

      try {
        const [facebook, twitter] = await Promise.all([
          this.$streemApiV1.get(`articles/${sibling.id}/facebook_count`),
          this.loadTweets(sibling.id),
        ]);

        this.socialCount = {
          facebook: facebook.data,
          twitter: twitter.headers["x-total-count"],
          total: facebook.data + Number(twitter.headers["x-total-count"]),
        };
      } finally {
        this.loadingSocial = false;
      }
    },
    async loadTweets() {
      const articleId = this.selectedSibling
        ? this.selectedSibling.id
        : this.onlineArticle.id;

      this.tweetFeedLoading = true;
      this.tweetsPage += 1;

      const { data, headers } = await this.$streemApiV1.get(
        `articles/${articleId}/tweets`,
        {
          params: {
            limit: 10,
            page: this.tweetsPage,
          },
        }
      );

      this.tweetFeedLoading = false;
      this.tweets = uniq([...this.tweets, ...data]);

      if (data.length < 10 && this.$refs.infiniteTweetScroll) {
        this.$refs.infiniteTweetScroll.stop();
      } else if (this.$refs.infiniteTweetScroll) {
        this.$refs.infiniteTweetScroll.resume();
      }

      return { data, headers };
    },
    async loadMoreTweets(index, done) {
      if (
        this.tweetFeedLoading ||
        (!this.tweetFeedLoading && !this.tweets.length)
      ) {
        this.$refs.infiniteTweetScroll.stop();

        return done();
      }

      try {
        await this.loadTweets();
      } finally {
        done();
      }

      return done();
    },
  },
};
</script>

<style lang="scss" scoped>
.download-button {
  background-color: $silver-white;
  color: #444;
  text-transform: none;
  font-size: 16px;
}

.logo {
  width: 40px;
  margin-right: 15px;
}

a {
  color: $primary;
}

.list-item-title {
  margin: 0 0.33em;
}

.circle {
  border-radius: 50%;
  color: #fff;
  font-size: 0.75em;
  width: 1.5em;
  height: 1.5em;
  position: relative;
  top: 0.1em;
}

.front-page {
  color: $active;
}

.front-page-icon {
  background-color: $active;
}

.social-shares {
  color: $social;
}

.social-icon {
  background-color: $social;
}

.embedded-tweet {
  position: relative;
}

.breaking {
  background-color: $active;
  color: white;
  font-weight: 600;
  border: 2px solid $active;
  border-radius: 0.15em;

  div {
    padding: 0 0.15em;
  }
}

.duration {
  background-color: white;
  color: $active;
  padding: 0 0.25em;
}
</style>
